import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatBC } from "@/lib/format";
import { Toaster, toast } from "sonner";
import { ArrowUpCircle, Clock, CheckCircle2, X, Lock, Fingerprint, PauseCircle } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_STYLE = {
  pending: { cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40", Icon: Clock, label: "Pending Payout" },
  released: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2, label: "Released" },
  rejected: { cls: "bg-[#DC2626]/15 text-[#FCA5A5] border-[#DC2626]/40", Icon: X, label: "Rejected" },
};

const PRESET_AMOUNTS = [500, 1000, 2500, 5000];

function QuickAmountRow({ minAmount, maxAvailable, onPick }) {
  const presets = PRESET_AMOUNTS.filter((v) => v >= minAmount);
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="wd-quick-amounts">
      {presets.map((v) => {
        const disabled = v > maxAvailable;
        return (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onPick(v)}
            className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/5 hover:bg-[#FBBF24]/15 hover:text-[#FBBF24] border border-white/10 hover:border-[#FBBF24]/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`wd-preset-${v}`}
          >
            ₱{v.toLocaleString()}
          </button>
        );
      })}
      {Number.isFinite(maxAvailable) && maxAvailable >= minAmount && (
        <button
          type="button"
          onClick={() => onPick(Math.floor(maxAvailable * 100) / 100)}
          className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300 transition-colors"
          data-testid="wd-preset-max"
        >
          Max
        </button>
      )}
    </div>
  );
}

export default function Withdrawals() {
  const { user, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ amount: "", channel: "gcash", account_name: user?.full_name || "", account_number: user?.mobile || "" });
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ withdrawal_fee_rate: 0.05, withdrawal_fee_label: "System Maintenance Fee" });
  const [cap, setCap] = useState(null);
  const [payoutsStateLocal, setPayoutsStateLocal] = useState({ paused: false, message: "" });

  const load = async () => { const { data } = await api.get("/withdrawals/mine"); setList(data); };
  const loadCap = async () => {
    try {
      const { data } = await api.get("/withdrawal-cap");
      setCap(data);
    } catch (e) {
      // Cap read is best-effort — the form still submits without a pre-cap render.
      console.warn("Withdrawal cap load failed:", e);
    }
  };
  const loadPayoutsState = async () => {
    try {
      const { data } = await api.get("/payouts/state");
      setPayoutsStateLocal(data);
    } catch (e) {
      console.warn("Payouts state load failed:", e);
    }
  };
  useEffect(() => {
    load();
    loadCap();
    loadPayoutsState();
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const requested = Number(form.amount || 0);
  const fee = +(requested * cfg.withdrawal_fee_rate).toFixed(2);
  const netPayout = +(requested - fee).toFixed(2);
  const minAmount = cap?.min_withdrawal_amount ?? 1000;
  const belowMin = requested > 0 && requested < minAmount;
  const exceedsCap = cap && requested > cap.available_today;
  const exceedsPersonal = cap && requested > (cap.my_available_today ?? Infinity);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals", { ...form, amount: Number(form.amount) });
      toast.success("Payout request locked. Admin will manually transfer to your wallet.");
      setForm({ ...form, amount: "" });
      await Promise.all([load(), refresh(), loadCap()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-7" data-testid="withdrawals-root">
      <Toaster position="top-right" richColors />
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Payout Gate</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Request Withdrawal</h1>
        <p className="text-white/60 mt-1 text-sm max-w-2xl">
          The moment you submit, the amount is moved into a Withdrawal Lock — untouchable while payout is in review.
          Admin will manually transfer via GCash/Maya/Bank and confirm release.
        </p>
      </div>

      {payoutsStateLocal.paused && (
        <div className="glass-card border border-[#DC2626]/50 bg-[#DC2626]/10 p-4 flex gap-3" data-testid="payouts-paused-banner">
          <PauseCircle className="text-[#FCA5A5] shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <div className="font-bold text-[#FCA5A5] uppercase tracking-widest text-xs">Payouts Paused</div>
            <div className="text-white/75 mt-1">{payoutsStateLocal.message}</div>
          </div>
        </div>
      )}

      {user?.kyc_status !== "approved" && (
        <div className="glass-card border border-[#DC2626]/40 bg-[#DC2626]/10 p-4 flex flex-wrap items-center gap-4" data-testid="wd-kyc-gate">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FCA5A5] font-bold">KYC Required</div>
            <div className="text-sm text-white/85 mt-1">
              Withdrawals are blocked until your identity is verified. Your payout account name must match your verified legal name.
            </div>
            {user?.kyc_status === "pending" && <div className="text-xs text-yellow-300 mt-1">Your submission is pending admin review.</div>}
            {user?.kyc_status === "rejected" && <div className="text-xs text-[#FCA5A5] mt-1">Your previous submission was rejected — please resubmit.</div>}
          </div>
          <Link to="/kyc" className="btn-deploy text-sm flex items-center gap-2" data-testid="wd-kyc-link">
            <Fingerprint size={14} /> {user?.kyc_status === "none" ? "Start KYC" : "Open KYC"}
          </Link>
        </div>
      )}

      {cap && (
        <div className="space-y-3">
          <div className="glass-card p-4 flex flex-wrap items-center gap-4" data-testid="cap-banner">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold">System Daily Cap</div>
              <div className="text-xs text-white/55 mt-1">
                {(cap.cap_percent * 100).toFixed(0)}% of yesterday&apos;s deployments
                {cap.allowance > 0 && <span> + ₱{formatBC(cap.allowance)} admin allowance</span>}
                . Resets at next UTC day.
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">Daily cap</div>
                <div className="font-mono-num text-[#FBBF24] text-lg" data-testid="cap-daily">₱{formatBC(cap.daily_cap)}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">Used today</div>
                <div className="font-mono-num text-lg" data-testid="cap-used">₱{formatBC(cap.used_today)}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">Available</div>
                <div className={`font-mono-num text-lg ${cap.available_today > 0 ? "text-[#FBBF24]" : "text-[#FCA5A5]"}`} data-testid="cap-available">₱{formatBC(cap.available_today)}</div>
              </div>
            </div>
          </div>

          <div className="glass-card p-4 flex flex-wrap items-center gap-4" data-testid="personal-cap-banner">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#93C5FD] font-bold">Your Personal Limit</div>
              <div className="text-xs text-white/55 mt-1">Each operator is capped at ₱{formatBC(cap.per_user_daily_limit)} BC per day (separate from the system cap).</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">Personal limit</div>
                <div className="font-mono-num text-[#93C5FD] text-lg" data-testid="my-cap-total">₱{formatBC(cap.per_user_daily_limit)}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">You used</div>
                <div className="font-mono-num text-lg" data-testid="my-cap-used">₱{formatBC(cap.my_used_today)}</div>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-[10px]">You can still withdraw</div>
                <div className={`font-mono-num text-lg ${cap.my_available_today > 0 ? "text-[#FBBF24]" : "text-[#FCA5A5]"}`} data-testid="my-cap-available">₱{formatBC(cap.my_available_today)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <form onSubmit={submit} className="glass-card p-6 lg:col-span-1 space-y-4">
          <div className="flex items-center gap-2 text-[#FBBF24]"><ArrowUpCircle /> <span className="font-display text-lg font-bold">New Payout</span></div>

          <div className="bg-black/30 rounded-md p-3 border border-white/10 text-xs flex justify-between">
            <span className="text-white/55">Available</span>
            <span className="font-mono-num text-[#FBBF24]" data-testid="wd-available">₱{formatBC(user?.active_balance)}</span>
          </div>

          <div>
            <label className="zfd-label">Channel</label>
            <select className="zfd-input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="wd-channel">
              <option value="gcash">GCash</option>
              <option value="maya">Maya</option>
              <option value="bank">Bank</option>
            </select>
          </div>
          <div>
            <label className="zfd-label">Account Name</label>
            <input required className="zfd-input" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} data-testid="wd-name" />
          </div>
          <div>
            <label className="zfd-label">Account / Mobile #</label>
            <input required className="zfd-input" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="wd-number" />
          </div>
          <div>
            <label className="zfd-label">Amount (₱ BC) — min ₱{formatBC(minAmount)}</label>
            <input type="number" min={minAmount} step="0.01" required className="zfd-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="wd-amount" />
            <QuickAmountRow
              minAmount={minAmount}
              maxAvailable={Math.min(
                user?.active_balance ?? 0,
                cap?.my_available_today ?? Infinity,
                cap?.available_today ?? Infinity,
              )}
              onPick={(v) => setForm((f) => ({ ...f, amount: String(v) }))}
            />
          </div>

          <div className="bg-black/40 border border-[#FBBF24]/30 rounded-md p-3 space-y-1.5 text-xs" data-testid="wd-fee-breakdown">
            <div className="flex justify-between text-white/65">
              <span>Requested</span>
              <span className="font-mono-num">₱{formatBC(requested)}</span>
            </div>
            <div className="flex justify-between text-[#FCA5A5]">
              <span>{cfg.withdrawal_fee_label} ({(cfg.withdrawal_fee_rate * 100).toFixed(0)}%)</span>
              <span className="font-mono-num" data-testid="wd-fee-preview">− ₱{formatBC(fee)}</span>
            </div>
            <div className="flex justify-between pt-1.5 border-t border-white/10">
              <span className="text-[#FBBF24] uppercase tracking-wider text-[10px] font-bold">You Receive</span>
              <span className="font-mono-num text-[#FBBF24] text-base" data-testid="wd-net-preview">₱{formatBC(netPayout)}</span>
            </div>
          </div>

          {belowMin && (
            <div className="text-[11px] text-[#FCA5A5] bg-[#DC2626]/10 border border-[#DC2626]/30 rounded p-2" data-testid="wd-min-warn">
              Minimum withdrawal is ₱{formatBC(minAmount)} BC per request.
            </div>
          )}
          {exceedsPersonal && (
            <div className="text-[11px] text-[#FCA5A5] bg-[#DC2626]/10 border border-[#DC2626]/30 rounded p-2" data-testid="wd-personal-warn">
              This exceeds your personal daily limit. You can still withdraw ₱{formatBC(cap?.my_available_today)} BC today.
            </div>
          )}
          {exceedsCap && (
            <div className="text-[11px] text-[#FCA5A5] bg-[#DC2626]/10 border border-[#DC2626]/30 rounded p-2" data-testid="wd-cap-warn">
              This amount exceeds today&apos;s available cap (₱{formatBC(cap?.available_today)}). Try a smaller amount or wait for next reset.
            </div>
          )}

          <button disabled={busy || exceedsCap || belowMin || exceedsPersonal || user?.kyc_status !== "approved" || payoutsStateLocal.paused} type="submit" className="btn-deploy w-full disabled:opacity-50" data-testid="wd-submit">
            {payoutsStateLocal.paused ? "Payouts currently paused" : busy ? "Locking…" : "Submit Payout Request"}
          </button>
        </form>

        <div className="glass-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold">My Payout History</h2>
            <div className="text-xs text-white/50 flex items-center gap-1">
              <Lock size={12} /> Locked: ₱{formatBC(user?.withdrawal_lock)}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                  <th className="text-left py-2">Submitted</th>
                  <th className="text-left">Channel</th>
                  <th className="text-left">Account</th>
                  <th className="text-left">Outgoing Ref</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Fee</th>
                  <th className="text-right">Net</th>
                  <th className="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-white/40 py-6" data-testid="wd-empty">No payout requests yet.</td></tr>
                )}
                {list.map((d) => {
                  const S = STATUS_STYLE[d.status] || STATUS_STYLE.pending;
                  const dFee = d.fee ?? +(d.amount * 0.05).toFixed(2);
                  const dNet = d.net_payout ?? +(d.amount - dFee).toFixed(2);
                  return (
                    <tr key={d.id} className="border-t border-white/5" data-testid={`wd-row-${d.id}`}>
                      <td className="py-2 text-xs text-white/70">{new Date(d.submitted_at).toLocaleString()}</td>
                      <td className="uppercase text-xs text-white/70">{d.channel}</td>
                      <td className="text-xs">{d.account_name}<br/><span className="text-white/40">{d.account_number}</span></td>
                      <td className="font-mono-num text-xs">{d.outgoing_reference || <span className="text-white/30">—</span>}</td>
                      <td className="text-right font-mono-num text-white/80">₱{formatBC(d.amount)}</td>
                      <td className="text-right font-mono-num text-[#FCA5A5]">− ₱{formatBC(dFee)}</td>
                      <td className="text-right font-mono-num text-[#FBBF24]">₱{formatBC(dNet)}</td>
                      <td className="text-right">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${S.cls}`}>
                          <S.Icon size={12} /> {S.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
